import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  XMarkIcon, 
  SunIcon, 
  MoonIcon, 
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { SearchableSelect } from './components/SearchableSelect';

// Definir interfaces para el formato correcto de JSON
interface Item {
  Item: string;
  ItemCatID: number;
  ItemIndex: number;
  ItemCat: string;
  ModelFile: string;
  Exc: boolean;
  SocketCount: boolean;
  Rate: string;
}

interface EventData {
  Event: string;
  Visible: boolean;
  Items: Item[];
}

function EventsPage() {
  // Estados para los datos y la UI
  const [events, setEvents] = useState<EventData[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<EventData[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventName, setSelectedEventName] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>(''); // Categoría seleccionada
  const [itemCategories, setItemCategories] = useState<string[]>([]); // Categorías únicas
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [darkMode, setDarkMode] = useState(true); // Preferencia del usuario
  const itemsPerPage = 12; // Número de eventos por página

  // Cargar eventos al iniciar
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        // Usar la URL correcta para eventos
        const response = await fetch('https://drops.elgasiamu.com/api/event-server.php');
        if (!response.ok) {
          throw new Error(`Error HTTP: ${response.status}`);
        }
        const data = await response.json();
        console.log('Datos recibidos:', data);
        setEvents(data);
        setFilteredEvents(data);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar eventos:', error);
        setLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Filtrar eventos cuando cambia el término de búsqueda, evento seleccionado o categoría
  useEffect(() => {
    if (!events.length) return;

    let result = [...events];

    // Filtrar por evento seleccionado
    if (selectedEventName) {
      result = result.filter(event => 
        event.Event.toLowerCase() === selectedEventName.toLowerCase()
      );
    }
    
    // Filtrar por categoría seleccionada
    if (selectedCategory) {
      result = result.map(event => ({
        ...event,
        Items: event.Items.filter(item => 
          item.ItemCat.toLowerCase() === selectedCategory.toLowerCase()
        )
      })).filter(event => event.Items.length > 0);
    }

    // Filtrar por término de búsqueda (buscar en nombres de ítems)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.map(event => ({
        ...event,
        Items: event.Items.filter(item => 
          item.Item.toLowerCase().includes(term) ||
          item.ItemCat.toLowerCase().includes(term)
        )
      })).filter(event => event.Items.length > 0);
    }

    setFilteredEvents(result);
    setPage(1);
  }, [searchTerm, selectedEventName, selectedCategory, events]);

  // Debug: Mostrar información sobre eventos
  useEffect(() => {
    console.log('Total de eventos cargados:', events.length);
    if (events.length > 0) {
      console.log('Eventos disponibles:', events.map(e => e.Event));
    }
  }, [events]);

  // Resetear filtros
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedEventName('');
    setSelectedCategory('');
  };

  // Obtener lista de eventos únicos para el selector
  const eventList = events.map(event => event.Event).sort();
  
  // Extraer categorías únicas de los ítems al cargar eventos
  useEffect(() => {
    if (events.length > 0) {
      // Extraer todas las categorías de los ítems
      const categories = new Set<string>();
      events.forEach(event => {
        event.Items.forEach(item => {
          if (item.ItemCat) {
            categories.add(item.ItemCat);
          }
        });
      });
      // Convertir a array y ordenar
      setItemCategories(Array.from(categories).sort());
    }
  }, [events]);
  
  // Paginación
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedEvents = filteredEvents.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);

  const handlePageChange = (value: number) => {
    setPage(value);
  };

  // Cambiar tema
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  // Componente para renderizar un icono individual desde archivos SVG (igual que en App.tsx)
  const ItemIcon = ({ iconName, className = "" }: { iconName: string; className?: string }) => {
    return (
      <img 
        src={`/_assets/icons/${iconName}.svg`} 
        className={`h-6 w-6 ${className}`}
        alt=""
        style={{ filter: 'brightness(0) invert(1)' }} // Hacer los iconos blancos
      />
    );
  };
  
  // Función para obtener ícono de categoría
  const getCategoryIcon = (categoryId: number) => {
    // Mapeo de categorías a iconos (basado en App.tsx)
    const iconMap: Record<number, string> = {
      0: 'icon_69', // Uncategorized
      1: 'icon_94', // Armor
      2: 'icon_164',  // Axes
      3: 'icon_158',  // Boots
      4: 'icon_163',  // Bow and Crossbows
      5: 'icon_157',  // Gloves
      6: 'icon_93',  // Helmets
      7: 'icon_170', // Jewel and Misc
      8: 'icon_162', // Mace and Scepters
      9: 'icon_166', // Moon
      10: 'icon_95', // Pants
      11: 'icon_170', // Ring and pets
      12: 'icon_90', // Shields
      13: 'icon_159', // Spears
      14: 'icon_160', // Staffs
      15: 'icon_171'  // Scrolls
    };
    
    // Obtener el nombre del icono o usar uno predeterminado si no existe
    const iconName = iconMap[categoryId] || 'icon_69';
    
    // Devolver el componente ItemIcon con el nombre del icono
    return <ItemIcon iconName={iconName} />;
  };

  // Renderizar contador de ítems
  const renderItemCount = () => {
    const totalItems = filteredEvents.reduce((sum, event) => sum + event.Items.length, 0);
    return (
      <div className="text-sm text-gray-400 dark:text-gray-500">
        {totalItems} {totalItems === 1 ? 'ítem' : 'ítems'} en {filteredEvents.length} {filteredEvents.length === 1 ? 'evento' : 'eventos'}
      </div>
    );
  };

  // Efecto para aplicar/quitar la clase dark al elemento HTML según el estado darkMode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      document.body.classList.add('light');
    }
  }, [darkMode]);
  
  return (
    <div className="min-h-screen dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Encabezado al estilo de DropList */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {/* Espacio para un logo */}
          </h1>
          <div className="flex items-center gap-4">
            <Link 
              to="/" 
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <ChevronLeftIcon className="h-5 w-5" />
              Volver al inicio
            </Link>
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg bg-gray-800 hover:bg-gray-700 transition-colors"
            >
              {darkMode ? (
                <SunIcon className="h-6 w-6 text-yellow-500" />
              ) : (
                <MoonIcon className="h-6 w-6 text-gray-300" />
              )}
            </button>
          </div>
        </div>

        {/* Título centrado con estilo más prominente */}
        <div className="flex justify-center items-center mb-8">
          <h1 className="text-4xl font-bold text-white drop-shadow-lg">
            Events Drops
          </h1>
        </div>

        {/* Filtros al estilo DropList - igual que en App.tsx */}
        <div className="sticky top-0 z-[60] rounded-lg bg-gray-900 text-white dark:bg-gray-950 px-4 py-4 my-4 space-y-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Input de búsqueda con dimensiones correctas */}
            <input
              type="text"
              placeholder="Search Item ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input flex-1"
            />
            
            {/* Selector de eventos */}
            <SearchableSelect
              options={eventList}
              value={selectedEventName}
              onChange={setSelectedEventName}
              placeholder="Select Event"
              label="Event"
            />
            
            {/* Botón resetear */}
            <button
              onClick={resetFilters}
              className="btn btn-secondary flex items-center gap-2"
            >
              <XMarkIcon className="h-5 w-5" />
              Reset
            </button>
          </div>
          
          {/* Barra de iconos de categorías exactamente como en DropList */}
          <div className="w-full bg-gray-900/90 border border-gray-800 rounded-lg py-2.5 px-3 mb-3">
            <div className="overflow-x-auto no-scrollbar">
              <div className="grid grid-flow-col auto-cols-max gap-3 px-2 py-2 justify-start items-center">
                {/* Elemento todos (sin filtro) */}
                <div 
                  onClick={() => setSelectedCategory('')}
                  title="Todas las categorías"
                  className={`flex items-center justify-center cursor-pointer transition-all duration-150 hover:scale-105 p-1.5 rounded-full ${selectedCategory === '' ? 'bg-primary-600/50 dark:bg-primary-800/70 ring-1 ring-primary-500' : 'hover:bg-gray-800'}`}
                >
                  <div className="h-9 w-9 flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-950 rounded-full border border-gray-600 shadow-inner shadow-black">
                    <Squares2X2Icon className="h-6 w-6 text-white drop-shadow-sm" />
                  </div>
                </div>
                
                {/* Iconos de CATEGORÍAS de ítems - no de eventos */}
                {itemCategories.slice(0, 15).map((category, index) => {
                  // Mapear el índice a un índice de icono (entre 0 y 15 para usar los iconos disponibles)
                  const iconIndex = index % 16;
                  
                  return (
                    <div 
                      key={category}
                      onClick={() => setSelectedCategory(selectedCategory === category ? '' : category)}
                      title={category} /* El tooltip muestra el nombre de la categoría */
                      className={`flex items-center justify-center cursor-pointer transition-all duration-150 hover:scale-105 p-1.5 rounded-full ${selectedCategory === category ? 'bg-primary-600/50 dark:bg-primary-800/70 ring-1 ring-primary-500' : 'hover:bg-gray-800'}`}
                    >
                      <div className="h-9 w-9 flex items-center justify-center bg-gradient-to-b from-gray-800 to-gray-950 rounded-full border border-gray-600 shadow-inner shadow-black">
                        {/* Usar el icono correspondiente a la categoría */}
                        {getCategoryIcon(iconIndex)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Texto informativo */}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredEvents.length} events
          </p>
        </div>

        {/* Banner de evento seleccionado */}
        {selectedEventName && (
          <div className="mb-6 bg-blue-100 dark:bg-blue-900 border-l-4 border-blue-500 p-4 rounded">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-blue-800 dark:text-blue-300">
                  {selectedEventName}
                </h2>
                {renderItemCount()}
              </div>
              <button
                onClick={() => setSelectedEventName('')}
                className="text-blue-500 hover:text-blue-700 dark:text-blue-300 dark:hover:text-blue-100"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Contenido principal */}
        <div className="mb-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="spinner"></div>
              <p className="mt-4 text-gray-600 dark:text-gray-400">Loading events...</p>
            </div>
          ) : paginatedEvents.length === 0 ? (
            <div className="text-center py-12 bg-gray-900 rounded-lg shadow border border-gray-800/30 flex flex-col items-center justify-center min-h-[300px]">
              <p className="text-white text-lg mb-2">No items found</p>
              <p className="text-gray-400 text-sm mb-6">Try with other search criteria</p>
              <button
                onClick={resetFilters}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6">
              {paginatedEvents.map((event) => (
                <div 
                  key={event.Event} 
                  className="bg-white/5 dark:bg-gray-800/30 backdrop-blur-md rounded-lg shadow-lg border border-gray-800/30 overflow-hidden"
                >
                  {/* Encabezado del evento como en DropList */}
                  <div className="bg-blue-900/70 px-4 py-2 border-b border-blue-700">
                    <h3 className="text-lg font-semibold text-white">
                      {event.Event} <span className="text-sm font-normal text-blue-200">({event.Items.length} {event.Items.length === 1 ? 'ítem' : 'ítems'})</span>
                    </h3>
                  </div>

                  {/* Lista de ítems */}
                  <div className="p-4">
                    <div className="grid grid-cols-8 gap-3 w-full">
                      {event.Items.map((item, index) => (
                        <div 
                          key={`${item.Item}-${index}`} 
                          className="relative"
                        >
                          {/* Badge de Socket si corresponde */}
                          {item.SocketCount && (
                            <div className="absolute top-1 left-1 z-30 text-xs bg-purple-600 text-white px-1.5 py-0.5 rounded-md font-medium">
                              Socket
                            </div>
                          )}
                            
                          {/* Contenedor de imagen del ítem exactamente como en DropList */}
                          <div className={`w-full aspect-square bg-black rounded-md border-2 ${item.Exc ? 'border-green-500' : item.SocketCount ? 'border-purple-500' : 'border-blue-500'} relative overflow-hidden h-28`}>
                            <img
                              src={`/items/${item.ItemCatID}-${item.ItemIndex}.gif`}
                              alt={item.Item.replace(/\d+$/, '')}
                              loading="lazy"
                              className="absolute inset-0 m-auto max-w-[80%] max-h-[80%] object-contain"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = '/placeholder.png';
                              }}
                            />
                          </div>

                          {/* Badge para Excellent */}
                          {item.Exc && (
                            <div className="absolute top-1 right-1 z-30 text-xs bg-green-600 text-white px-1.5 py-0.5 rounded-md font-medium">
                              Excellent Item
                            </div>
                          )}

                          {/* Nombre del ítem - estilo exacto de DropList */}
                          <div className="text-center mt-2">
                            <p className="text-sm font-medium text-white truncate">{item.Item}</p>
                            <p className="text-xs text-gray-500">{item.ItemCat}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Paginación igual a DropList */}
        {totalPages > 1 && (
          <div style={{ position: 'fixed', bottom: '0', left: '0', right: '0', zIndex: 50, display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
            <nav className="flex items-center gap-2 bg-gray-800/70 p-4 rounded-xl shadow-lg border border-gray-700/50">
              {/* Botón Anterior */}
              <button
                onClick={() => page > 1 && handlePageChange(page - 1)}
                disabled={page === 1}
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 ${page === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-600 hover:text-white'} bg-gray-700 text-gray-300`}
              >
                <ChevronLeftIcon className="h-5 w-5" />
              </button>
              
              {/* Números de página */}
              <div className="flex items-center">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                  // Mostrar solo página actual, primera, última y 1 o 2 páginas adyacentes
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= page - 1 && pageNum <= page + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 mx-1 font-medium rounded-full transition-all duration-200 ${
                          page === pageNum
                            ? 'bg-primary-600 text-white shadow-md shadow-primary-500/30 ring-2 ring-primary-400/50 transform scale-110'
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600 dark:hover:bg-gray-600 hover:text-white'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (
                    (pageNum === 2 && page > 3) ||
                    (pageNum === totalPages - 1 && page < totalPages - 2)
                  ) {
                    // Mostrar puntos suspensivos
                    return (
                      <span key={pageNum} className="mx-1 text-gray-400 dark:text-gray-500">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}
              </div>
              
              {/* Botón Siguiente */}
              <button
                onClick={() => page < totalPages && handlePageChange(page + 1)}
                disabled={page === totalPages}
                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-200 ${page === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary-600 hover:text-white'} bg-gray-700 text-gray-300`}
              >
                <ChevronRightIcon className="h-5 w-5" />
              </button>
            </nav>
          </div>
        )}
        
        {/* Efecto de blur progresivo usando las clases CSS */}
        {totalPages > 1 && (
          <div className="gradient-blur">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        )}
        
        {/* Espacio adicional para evitar que el contenido se solape con la paginación fija */}
        {totalPages > 1 && <div className="h-24"></div>}
      </div>
    </div>
  );
}

export default EventsPage;
